const RELOAD_GUARD_KEY = 'legacy_sw_cleanup_reloaded';
const LEGACY_WORKER_PATH = 'ngsw-worker.js';

function isLegacyWorker(registration: ServiceWorkerRegistration): boolean {
  const scriptURL =
    registration.active?.scriptURL ||
    registration.waiting?.scriptURL ||
    registration.installing?.scriptURL ||
    '';

  return scriptURL.includes(LEGACY_WORKER_PATH);
}

/**
 * Removes the Angular service worker (ngsw-worker.js) left registered on this origin
 * by the pre-migration app. Without this, returning users keep getting the cached
 * Angular index.html and its stale hashed bundles instead of the React build.
 *
 * Matched by script path so a service worker intentionally added later at a
 * different path is not unregistered by this cleanup.
 */
export async function removeLegacyServiceWorkers(): Promise<boolean> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return false;
  }

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    const legacyRegistrations = registrations.filter(isLegacyWorker);

    if (legacyRegistrations.length === 0) {
      return false;
    }

    await Promise.all(legacyRegistrations.map((registration) => registration.unregister()));

    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)));
    }

    // The current document may still be controlled by the now-unregistered worker,
    // so reload once to guarantee everything comes from the network.
    if (!sessionStorage.getItem(RELOAD_GUARD_KEY)) {
      sessionStorage.setItem(RELOAD_GUARD_KEY, 'true');
      window.location.reload();
      return true;
    }

    return false;
  } catch {
    // Cleanup must never block application startup.
    return false;
  }
}
